import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AS_HALF_PRODUCT_QUANTITY } from '../constants/common';
import { findNearestValueByMOQ, getWholeNumberOrTwoDecimal } from '../utils/product.util';
import { BASKET_UPDATE_ITEM_TRIGGER_TYPE } from '../constants/analytics/commonAttribute';

const INCREMENT_DECREMENT_INTERVAL = 200;
const SINGLE_PRESS_THRESHOLD = 300;
const DEBOUNCE_DELAY = 100;

type QuantityPickerProps = {
  disabled: boolean;
  initialValue: number;
  max: number;
  onIncrement: (e: SyntheticEvent) => void;
  onDecrement: (e: SyntheticEvent) => void;
  onChange: (quantity: number, actionType: string) => Promise<boolean>;
  productId?: string;
  showKg?: boolean;
  moq?: number;
  isPDP?: boolean;
  isFavList?: boolean;
  isAddToCart?: boolean;
  enablePartialQuantity?: boolean;
  restrictDeleteFunctionality?: boolean;
  onDelete?: () => void;
  size?: 'mini' | 'small' | 'medium' | 'large';
  callApiOnChange?: boolean;
  isLoggedIn?: boolean;
  onCloseModal?: () => void;
};

export const useQuantityPicker = (props: QuantityPickerProps) => {
  const {
    initialValue,
    max,
    moq = 1,
    showKg,
    enablePartialQuantity,
    restrictDeleteFunctionality = false,
    onChange,
    onIncrement,
    onDecrement,
    onDelete,
    disabled,
    callApiOnChange = true,
    onCloseModal,
  } = props;

  const [value, setValue] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const isPressAndHoldEvent = useRef(false);
  const buttonPressedRef = useRef(false);
  const incrementIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const decrementIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef(0);
  const lastClickTimeRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  const minimumQuantity = useMemo(
    () => (enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : showKg ? moq : restrictDeleteFunctionality ? 0 : 1),
    [enablePartialQuantity, showKg, moq, restrictDeleteFunctionality],
  );

  const stepQuantity = useMemo(
    () => (enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : showKg || moq > 1 ? moq : 1),
    [enablePartialQuantity, showKg, moq],
  );

  const maximumQuantity = useMemo(() => (showKg ? max * moq : max), [showKg, max, moq]);

  const formattedValue = useMemo(() => (showKg ? parseFloat(value.toString()) : Number(value)), [showKg, value]);

  useEffect(() => {
    if (!isUpdating && !isInputFocused && initialValue !== undefined) {
      setValue(getWholeNumberOrTwoDecimal(!showKg ? initialValue : initialValue * moq));
    }
  }, [initialValue, isUpdating, moq, restrictDeleteFunctionality, showKg, isInputFocused]);

  useEffect(() => {
    if (!isInputFocused && inputRef.current) {
      inputRef.current.blur();
    }
  }, [isInputFocused]);

  useEffect(() => {
    return () => {
      clearInterval(incrementIntervalRef.current as NodeJS.Timeout);
      clearInterval(decrementIntervalRef.current as NodeJS.Timeout);
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
      buttonPressedRef.current = false;
      isPressAndHoldEvent.current = false;
    };
  }, []);

  const displayTooltip = useCallback(() => {
    setShowTooltip(true);
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 3000);
  }, []);

  const clearIncrementInterval = useCallback(() => {
    if (incrementIntervalRef.current) {
      clearInterval(incrementIntervalRef.current);
      incrementIntervalRef.current = null;
    }
  }, []);

  const clearDecrementInterval = useCallback(() => {
    if (decrementIntervalRef.current) {
      clearInterval(decrementIntervalRef.current);
      decrementIntervalRef.current = null;
    }
  }, []);

  const updateQuantityOnRelease = useCallback(
    async (actionType?: string) => {
      if (isUpdating || disabled) return;

      setIsUpdating(true);
      try {
        clearIncrementInterval();
        clearDecrementInterval();

        let valueInKg = value;
        if (enablePartialQuantity) {
          valueInKg = findNearestValueByMOQ(AS_HALF_PRODUCT_QUANTITY, value);
        } else if (showKg) {
          valueInKg = findNearestValueByMOQ(moq, value);
        }

        const updated = callApiOnChange && (await onChange(showKg ? valueInKg / moq : valueInKg, actionType || ''));
        if (!updated || showKg) setValue(valueInKg);
        setIsInputFocused(false);
      } finally {
        setIsUpdating(false);
      }
    },
    [
      value,
      showKg,
      moq,
      enablePartialQuantity,
      onChange,
      clearDecrementInterval,
      clearIncrementInterval,
      isUpdating,
      disabled,
      callApiOnChange,
    ],
  );

  useEffect(() => {
    if (
      (decrementIntervalRef.current && value === minimumQuantity) ||
      (incrementIntervalRef.current && value >= maximumQuantity)
    ) {
      updateQuantityOnRelease();
    }
  }, [value, maximumQuantity, minimumQuantity, updateQuantityOnRelease]);

  const incrementCounter = useCallback(() => {
    if (isUpdating || disabled) return;
    setValue((prev) => {
      const base = prev < minimumQuantity ? minimumQuantity : prev;
      const nextVal = base + stepQuantity;

      return nextVal >= maximumQuantity ? maximumQuantity : getWholeNumberOrTwoDecimal(nextVal);
    });
  }, [maximumQuantity, minimumQuantity, stepQuantity, isUpdating, disabled]);

  const decrementCounter = useCallback(() => {
    if (isUpdating || disabled) return;
    setValue((prev) => {
      const nextVal = prev - stepQuantity;
      return nextVal <= minimumQuantity ? minimumQuantity : getWholeNumberOrTwoDecimal(nextVal);
    });
  }, [minimumQuantity, stepQuantity, isUpdating, disabled]);

  const handleIncrementMouseDown = useCallback(() => {
    if (isUpdating || disabled) return;
    clearIncrementInterval();
    buttonPressedRef.current = true;
    startTimeRef.current = Date.now();
    incrementIntervalRef.current = setInterval(incrementCounter, INCREMENT_DECREMENT_INTERVAL);
  }, [clearIncrementInterval, incrementCounter, isUpdating, disabled]);

  const handleDecrementMouseDown = useCallback(
    (e: SyntheticEvent) => {
      if (isUpdating || disabled) return;

      if (value === minimumQuantity && callApiOnChange) {
        onDecrement(e);
      } else {
        clearDecrementInterval();
        buttonPressedRef.current = true;
        startTimeRef.current = Date.now();
        decrementIntervalRef.current = setInterval(decrementCounter, INCREMENT_DECREMENT_INTERVAL);
      }
    },
    [
      value,
      minimumQuantity,
      onDecrement,
      clearDecrementInterval,
      decrementCounter,
      isUpdating,
      disabled,
      callApiOnChange,
    ],
  );

  const handleMouseUp = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();
      if (disabled) return;

      clearIncrementInterval();
      clearDecrementInterval();
      buttonPressedRef.current = false;

      const timeDiff = Date.now() - startTimeRef.current;
      if (timeDiff >= SINGLE_PRESS_THRESHOLD) {
        isPressAndHoldEvent.current = true;
        if (callApiOnChange) {
          await updateQuantityOnRelease(BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON_HOLD);
        }
      }
    },
    [clearIncrementInterval, clearDecrementInterval, updateQuantityOnRelease, disabled, callApiOnChange],
  );

  const handleContextMenu = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
  }, []);

  const handleDecrementMouseLeave = useCallback(() => {
    if (buttonPressedRef.current) clearDecrementInterval();
    buttonPressedRef.current = false;
    isPressAndHoldEvent.current = false;
  }, [clearDecrementInterval]);

  const handleIncrementMouseLeave = useCallback(() => {
    if (buttonPressedRef.current) clearIncrementInterval();
    buttonPressedRef.current = false;
    isPressAndHoldEvent.current = false;
  }, [clearIncrementInterval]);

  const debouncedIncrement = useCallback(
    (e: SyntheticEvent) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current < DEBOUNCE_DELAY) return;
      lastClickTimeRef.current = now;

      if (!isPressAndHoldEvent.current) {
        if (callApiOnChange) {
          onIncrement(e);
        } else {
          incrementCounter();
        }
      }
      isPressAndHoldEvent.current = false;
    },
    [onIncrement, callApiOnChange, incrementCounter],
  );

  const debouncedDecrement = useCallback(
    (e: SyntheticEvent) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current < DEBOUNCE_DELAY) return;
      lastClickTimeRef.current = now;

      if (!isPressAndHoldEvent.current) {
        if (callApiOnChange) {
          onDecrement(e);
        } else if (value > minimumQuantity) {
          decrementCounter();
        } else {
          onDecrement(e);
        }
      }
      isPressAndHoldEvent.current = false;
    },
    [onDecrement, callApiOnChange, decrementCounter, value, minimumQuantity],
  );

  const handleDelete = useCallback(() => {
    if (!isUpdating) {
      onDelete?.();
      onCloseModal?.();
    }
  }, [onDelete, isUpdating, onCloseModal]);

  const handleInputChange = useCallback(
    (inputValue: string, shouldShowTooltip = true) => {
      const newValue = inputValue ? parseFloat(inputValue) : 0;
      let adjustedValue: number;
      if (enablePartialQuantity) {
        adjustedValue = Math.round(newValue * 2) / 2;
      } else if (showKg) {
        adjustedValue = getWholeNumberOrTwoDecimal(newValue);
      } else {
        adjustedValue = Math.round(newValue);
      }
      if (!isNaN(adjustedValue)) {
        if (adjustedValue >= maximumQuantity) {
          setValue(getWholeNumberOrTwoDecimal(maximumQuantity));
          if (shouldShowTooltip) displayTooltip();
        } else {
          setValue(adjustedValue);
        }
      }
    },
    [enablePartialQuantity, showKg, maximumQuantity, displayTooltip],
  );

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(async () => {
    await updateQuantityOnRelease(BASKET_UPDATE_ITEM_TRIGGER_TYPE.KEYBOARD);
  }, [updateQuantityOnRelease]);

  const preventDefault = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  }, []);

  const resetValue = useCallback(() => {
    setValue(getWholeNumberOrTwoDecimal(!showKg ? initialValue : initialValue * moq));
  }, [initialValue, showKg, moq]);

  const setValueDirectly = useCallback((newValue: number) => {
    setValue(getWholeNumberOrTwoDecimal(newValue));
  }, []);

  return {
    value,
    formattedValue,
    minimumQuantity,
    maximumQuantity,
    isUpdating,
    isInputFocused,
    showTooltip,
    inputRef,
    handleDelete,
    handleDecrement: debouncedDecrement,
    handleDecrementMouseDown,
    handleMouseUp,
    handleDecrementMouseLeave,
    handleContextMenu,
    handleIncrement: debouncedIncrement,
    handleIncrementMouseDown,
    handleIncrementMouseLeave,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    preventDefault,
    resetValue,
    setValueDirectly,
  };
};
